"use client"

import type React from "react"
import { useEffect, useRef } from "react"
import dynamic from "next/dynamic"
import { useAuthStore } from "@/lib/auth-store"
import { useFavoritesStore } from "@/lib/favorites-store"
import { createClient } from "@/lib/supabase/client"
import { toast } from "sonner"

import type { AuthChangeEvent, Session } from "@supabase/supabase-js"
import { useTranslations } from "next-intl"
import { DailyGooncoinClaimPopup } from "@/components/gooncoin/daily-gooncoin-claim-popup"

const UserSyncProvider = dynamic(
  () => import("@/components/auth/user-sync-provider").then((mod) => mod.UserSyncProvider),
  { ssr: false },
)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const t = useTranslations("authProvider")
  const initialize = useAuthStore((state) => state.initialize)
  const setUser = useAuthStore((state) => state.setUser)
  const migrateFavorites = useFavoritesStore((state) => state.migrateGuestFavorites)
  const setFavoritesLoggedIn = useFavoritesStore((state) => state.setLoggedIn)
  const initialized = useRef(false)
  const userId = useAuthStore((state) => state.user?.id ?? null)

  useEffect(() => {
    if (initialized.current) return
    initialized.current = true

    console.log('[AUTH PROVIDER] Mounting — calling initialize()')
    initialize()

    const supabase = createClient()
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event: AuthChangeEvent, session: Session | null) => {
      console.log('[AUTH PROVIDER] onAuthStateChange fired:', event,
        '| user:', session?.user?.email ?? 'none',
        '| session expires:', session?.expires_at ? new Date(session.expires_at * 1000).toISOString() : 'n/a'
      )

      const currentState = useAuthStore.getState()
      console.log('[AUTH PROVIDER] Current store state before handling event:',
        'user=', currentState.user?.email ?? 'null',
        'role=', currentState.profile?.role ?? 'null',
        'isGuest=', currentState.isGuest
      )

      if (event === "INITIAL_SESSION") {
        console.log('[AUTH PROVIDER] INITIAL_SESSION — validating session matches current state')
        
        // If we have a session but it doesn't match our current user, update it
        if (session?.user) {
          const currentUser = currentState.user
          if (!currentUser || currentUser.id !== session.user.id) {
            console.log('[AUTH PROVIDER] Session mismatch detected — updating to session user:', session.user.email)
            setUser(session.user)
            await useAuthStore.getState().refreshProfile(session.user)
            await migrateFavorites(session.user.id)
            setFavoritesLoggedIn(true)
          } else {
            console.log('[AUTH PROVIDER] Session matches current user — no action needed')
          }
        }
        return
      }

      if (event === "PASSWORD_RECOVERY") {
        console.log('[AUTH PROVIDER] PASSWORD_RECOVERY - sending user to password reset')
        if (session?.user) {
          setUser(session.user)
          await useAuthStore.getState().refreshProfile(session.user)
        }
        if (window.location.pathname !== "/auth/reset-password") {
          window.location.replace("/auth/reset-password")
        }
        return
      }

      if (event === "SIGNED_IN") {
        console.log('[AUTH PROVIDER] SIGNED_IN — setting user and refreshing profile')
        setUser(session!.user)
        await useAuthStore.getState().refreshProfile(session!.user)
        await migrateFavorites(session!.user.id)
        setFavoritesLoggedIn(true)

        // Check for pending referral code (stored when user visited ?ref=CODE)
        const pendingReferralCode = localStorage.getItem('goonru_referral_code')
        if (pendingReferralCode) {
          localStorage.removeItem('goonru_referral_code')
          fetch('/api/referrals/track', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ referralCode: pendingReferralCode }),
          }).then(async r => {
            const data = await r.json()
            if (r.ok) {
              console.log('[AUTH PROVIDER] Referral tracked:', pendingReferralCode)
            } else {
              console.log('[AUTH PROVIDER] Referral not tracked:', data.error)
            }
          }).catch(e => console.error('[AUTH PROVIDER] Referral tracking failed:', e))
        }

        // Claim any widget commissions (guest/no-account orders) matching this confirmed email —
        // without this, signing up never connected a guest's past or future widget orders to
        // their real account; they'd be stuck relying on the emailed tracking link forever.
        fetch('/api/widget/claim', { method: 'POST' })
          .then(async r => {
            if (!r.ok) return
            const data = await r.json()
            if (data.claimed > 0) {
              toast.success(
                data.claimed === 1 ? t("linked1CommissionToYourAccount") : t("linkedClaimedCommissionsToYourAccount", { claimed: data.claimed }),
              )
            }
          })
          .catch(e => console.error('[AUTH PROVIDER] Widget commission claim failed:', e))

      } else if (event === "TOKEN_REFRESHED") {
        console.log('[AUTH PROVIDER] TOKEN_REFRESHED — refreshing profile to ensure role is current')
        if (session?.user) {
          setUser(session.user)
          await useAuthStore.getState().refreshProfile(session.user)
        }

      } else if (event === "SIGNED_OUT") {
        console.log('[AUTH PROVIDER] SIGNED_OUT — clearing user and profile')
        setUser(null)
        useAuthStore.getState().setProfile(null)
        setFavoritesLoggedIn(false)

      } else if (event === "USER_UPDATED") {
        console.log('[AUTH PROVIDER] USER_UPDATED — refreshing profile')
        if (session?.user) {
          setUser(session.user)
          await useAuthStore.getState().refreshProfile()

          // Email confirmation lands as USER_UPDATED, not another SIGNED_IN — if the SIGNED_IN
          // claim attempt above ran before the user confirmed their email, this is what
          // actually catches it once they do.
          if (session.user.email_confirmed_at) {
            fetch('/api/widget/claim', { method: 'POST' })
              .then(async r => {
                if (!r.ok) return
                const data = await r.json()
                if (data.claimed > 0) {
                  toast.success(
                    data.claimed === 1 ? t("linked1CommissionToYourAccount") : t("linkedClaimedCommissionsToYourAccount", { claimed: data.claimed }),
                  )
                }
              })
              .catch(e => console.error('[AUTH PROVIDER] Widget commission claim failed:', e))
          }
        }

      } else {
        console.log('[AUTH PROVIDER] Unhandled event:', event)
      }

      const afterState = useAuthStore.getState()
      console.log('[AUTH PROVIDER] Store state AFTER handling', event, ':',
        'user=', afterState.user?.email ?? 'null',
        'role=', afterState.profile?.role ?? 'null',
        'isGuest=', afterState.isGuest
      )
    })

    return () => {
      console.log('[AUTH PROVIDER] Unmounting — unsubscribing auth listener')
      subscription.unsubscribe()
    }
  }, [])

  return (
    <>
      {children}
      {userId && <UserSyncProvider />}
      {userId && <DailyGooncoinClaimPopup />}
    </>
  )
}
